import * as React from 'react'
import { connect } from 'react-redux'
import { Badge, badges } from '@amzn/dvui/dist/Badge'
import { Icon, IconType } from '@amzn/dvui/dist/Icon'
import { Packshot } from '@amzn/dvui/dist/Packshot'
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { IDetailPageState } from '../../reducers'
import { sanitizedRequestContextSelector } from '../../selectors/requestContext'
import * as st from './carouselCollectionItem.module.scss'
import { buildDetailPageUrl } from '../../utils/url'
import { isDisrupted } from '../../utils/liveState'
import { hasAny, safeBoolean } from '../../utils/utils'
import { SrcSize } from '../../utils/imageUtils'
import { ResponsiveImage } from '../images/responsiveImage'
import { isHoverableTitle } from '../../utils/titleTypes'
import { DETAIL_FRAGMENT } from '../../reducers/detail'
import { isLocalRatingBadge } from '../../utils/ratingBadgeHelper'
import { MaturityRatingProps, StorefrontItemLive } from '@utils'

const Hover = React.lazy(
  () => import(/* webpackChunkName: "detail-hover" */ '../../../Explore/DVWebNodeHover')
  .then(({ Hover }) => ({ default: Hover }))
)

const DEFAULT_WIDTH = 329
const TABLET_WIDTH = 266
const MOBILE_WIDTH = 216

const ENABLE_HOVER_CLASS = "dv-universal-hover-enabled"
const SIMS_CARD_REFTAG_PREFIX = "atv_dp_sims_c_"

interface OwnProps {
  idx: number
  titleId: string
  refTagPrefix?: string
  requestFocusForCarousel?: () => void
  resetFocusForCarousel?: () => void
}

interface CarouselItemState {
  isHoverReady: boolean
}

type StateProps = ReturnType<typeof mapStateToProps>

type Props = StateProps & OwnProps

const FloatingBadges = ({ detail}: {
  detail: WpsRuntime.atv.wps.DetailFragmentModelProps
}) => {
  const isLive = detail.liveState && safeBoolean(detail.liveState.isLive)
  const text = detail.liveState && detail.liveState.text
  const liveStateId = detail.liveState && detail.liveState.id
  const dateTimeBadge = detail.dateTimeBadge
  const badgeType = isLive ? badges.LIVE : badges.CRITICAL

  return (<div className={st.packshotBadges}>
    {
      (isDisrupted(liveStateId) || isLive) ?
        text && <span className={st.packshotBadge}>
          <Badge use={badgeType}>{text}</Badge>
        </span> :
        dateTimeBadge && <span className={st.packshotBadge}>
          <Badge use={badges.WHITE}>{dateTimeBadge}</Badge>
        </span>
    }
  </div>)
}

class ConnectedCarouselCollectionItem  extends React.PureComponent<Props, CarouselItemState>  {
  constructor(props: Props) {
    super(props)
    this.state = {
      isHoverReady: false
    }
  }

  componentDidMount() {
    if (!this.state.isHoverReady) {
      this.setState({
        isHoverReady: true
      })
    }
  }

  render() {
    const { detail, imdb, titleId, idx,
            requestContext, pageContext, pageLink,
            watchlistAction, refTagPrefix, isElcano,
            resetFocusForCarousel, requestFocusForCarousel} = this.props
    const { isHoverReady } = this.state

    const isRetail = requestContext.isRetail

    if (!detail) {
      return null
    }

    // Falling back to titleId just to be safe in case Swift fail to return asin/gti
    const detailTitleId = (isRetail ? detail.asin : detail.compactGti) || titleId
    const hoverTitleId = (isRetail ? detail.asin : detail.gti) || isHoverableTitle(titleId) ? titleId : undefined
    const refTag = refTagPrefix ? `${refTagPrefix}${idx + 1}` : `${SIMS_CARD_REFTAG_PREFIX}${idx + 1}`
    const detailUrl = buildDetailPageUrl(
      {
        parameters: requestContext.queryParameters,
        originalURI: requestContext.originalURI,
        isRetail
      }, detailTitleId, refTag)

    const pageUrl = pageLink || detailUrl
    const isPrime = (isRetail || isElcano) && safeBoolean(detail.isPrime) // We only show prime badge on Retail or on RoW in Elcano context
    const covershot = detail.images && detail.images.covershot

    const imageProps = {
      alt: detail.title,
      src: covershot,
      baseValue: DEFAULT_WIDTH,
      srcSizes: [
        { 'max-tablet-landscape': TABLET_WIDTH },
        { 'max-phone-landscape': MOBILE_WIDTH },
      ] as SrcSize[],
      skipStripTags: true, // tags for 16x9 added in wps to blur background for fallback
      isLazy: true,
    }

    const packshotProps = {
      url: pageUrl,
      isPrime
    }

    const enableHover = hoverTitleId && pageContext.enableHover
    const enableHoverClass = enableHover ? ENABLE_HOVER_CLASS : undefined

    const hoverContent = <div className={st.packshot}>
      <Packshot {...packshotProps} className={enableHoverClass} asin={hoverTitleId}>
        <div className={st.imageWrapper}>
          <div className={st.fallbackPackshot}>
            <Icon use={IconType.SMILE} />
          </div>
          {covershot && <ResponsiveImage className={st.image} classNameContainer={st.image} {...imageProps} />}
        </div>
      </Packshot>
      <FloatingBadges detail={detail}/>
    </div>

    const liveBadgeLabel = (detail.liveState && safeBoolean(detail.liveState.isLive)) ? detail.liveState.text : null

    const itemLive = (detail.liveState || detail.dateTimeBadge || detail.location) && {
      status: detail.liveState && detail.liveState.id,
      label: detail.liveState && detail.liveState.text,
      timeBadge: detail.dateTimeBadge,
      venue: detail.location
    } as StorefrontItemLive

    const ratingBadge = detail.ratingBadge
    const contentMaturityRating = ratingBadge ?
      isLocalRatingBadge(ratingBadge) ?
        {
          rating: ratingBadge.simplifiedId,
          locale: ratingBadge.countryCode.toLowerCase(),
          title: ratingBadge.description,
          link: ratingBadge.localRatingLink
        } as MaturityRatingProps : {
          rating: ratingBadge.displayText,
          title: ratingBadge.description,
          link: ratingBadge.localRatingLink
        } as MaturityRatingProps
      : undefined

    const customerReviews = isRetail && detail.amazonRating
    const imdbRating = imdb && imdb.scoreFormatted

    if (!isHoverReady || !enableHover) {
      return hoverContent
    }

    return (
      <React.Suspense fallback={hoverContent}>
        <Hover
          className={st.hover}
          title={detail.title}
          titleID={hoverTitleId}
          synopsis={detail.synopsis}
          liveBadgeLabel={liveBadgeLabel}
          imdbRating={imdbRating}
          releaseYear={detail.releaseYear}
          runtime={detail.runtime}
          hasSubtitles={hasAny(detail.subtitles)}
          watchlistAction={watchlistAction}
          customerReviews={customerReviews}
          liveInfo={itemLive}
          contentMaturityRating={contentMaturityRating}
          onMouseOver={() => requestFocusForCarousel && requestFocusForCarousel()}
          onMouseLeave={() => resetFocusForCarousel && resetFocusForCarousel()}
        >
          {hoverContent}
        </Hover>
      </React.Suspense>
    )
  }

}

const mapStateToProps = (
  state: IDetailPageState,
  ownProps: OwnProps
) => ({
  detail: state.detail[DETAIL_FRAGMENT][ownProps.titleId]!,
  imdb: state.imdb[ownProps.titleId],
  watchlistAction: state.hoverWatchlist[ownProps.titleId],
  requestContext: sanitizedRequestContextSelector(state),
  pageContext: state.pageContext,
  pageLink: state.pageLink[ownProps.titleId],
  isElcano: Boolean(state.features.isElcano),
})

export const CarouselCollectionItem = connect(mapStateToProps)(ConnectedCarouselCollectionItem)